import React, { useEffect, useState } from "react"
import { number, shape, string, array } from "prop-types"
import { ASSIGNMENT_KIND } from "components/admin/FilterBar/AssignmentOptions"
import CheckInsList from "components/admin/AttendanceDropdown/CheckInsList"
import { Link, Text, Heading } from "@planningcenter/tapestry-react"
import useFlipperFlag from "hooks/use_flipper_flag"

const LegacySessions = ({
  apiBaseUrl,
  checkInsBaseUrl,
  event,
  filterOptions,
  checkInsPermissions
}) => {
  const [existingLegacyEvents, setExistingLegacyEvents] = useState([])
  const [newEvents, setNewEvents] = useState([])

  const assignmentTypes = filterOptions[ASSIGNMENT_KIND]
  const checkistrationsV2Enabled = useFlipperFlag("ROLLOUT_checkistrations_v2")

  useEffect(() => {
    const fetchCheckinsData = async () => {
      const response = await fetch(
        `${apiBaseUrl}/events?where[registration_event_id]=${event.id}&order=name`,
        {
          credentials: "include"
        }
      )

      const checkins = await response.json().data
      const dividedAndDecoratedEvents = divideCheckIns(checkins)
      setExistingLegacyEvents(dividedAndDecoratedEvents.existingList)
      setNewEvents(dividedAndDecoratedEvents.newList)
    }

    if (["editor", "viewer"].includes(checkInsPermissions)) {
      fetchCheckinsData()
    }
  }, [])

  const decorateCheckInEvent = checkInEvent => {
    const result = {}
    const assignmentRegex = checkInEvent.attributes.integration_key.match(/assignment-type:(\d+)\|/)

    if (assignmentRegex && assignmentRegex.length > 1) {
      result.assignmentId = assignmentRegex[1]
      // Assuming `assignmentTypes` is an array of assignmentType objects {id, name, ...}
      const assignmentTypeParams = assignmentTypes
        .map(assignmentType => `assignment_type_ids[]=${encodeURIComponent(assignmentType.id)}`)
        .join("&")

      result.url = `${checkInsBaseUrl}/registrations/?event_id=${event.id}&assignment_type_id=${result.assignmentId}&${assignmentTypeParams}`
    } else {
      result.rootEvent = true
      result.url = `${checkInsBaseUrl}/registrations/?event_id=${event.id}`
    }

    result.name = checkInEvent.attributes.name
    return result
  }

  const divideCheckIns = (checkInEvents = []) => {
    const existingResults = checkInEvents.map(checkInEvent => decorateCheckInEvent(checkInEvent))

    const newResults = []
    const rootEventIsPresent = existingResults.find(er => er.rootEvent === true)

    if (!rootEventIsPresent) {
      newResults.push({
        url: `${checkInsBaseUrl}/registrations/?event_id=${event.id}&event_name=${event.name}&integration_level=Event`,
        name: event.name
      })
    }

    if (checkistrationsV2Enabled) {
      const assignmentTypeParams = assignmentTypes
        .map(assignmentType => `assignment_type_ids[]=${encodeURIComponent(assignmentType.id)}`)
        .join("&")

      newResults.push({
        url: `${checkInsBaseUrl}/registrations/?event_id=${event.id}&event_name=${event.name}&integration_level=AssignmentType&sync_future_assignment_types=1&${assignmentTypeParams}`,
        name: `${event.name} - All Assignments`
      })
    }

    assignmentTypes.forEach(assignment => {
      const matchingAssignmentInExistingList = existingResults.find(
        entry => entry.assignmentId === assignment.id.toString()
      )

      if (!matchingAssignmentInExistingList) {
        newResults.push({
          url: `${checkInsBaseUrl}/registrations/?event_id=${event.id}&event_name=${
            event.name
          }&assignment_type_id=${
            assignment.id
          }&sync_future_assignment_types=0&integration_level=AssignmentType&assignment_type_ids[]=${encodeURIComponent(
            assignment.id
          )}`,
          name: `${event.name}: ${assignment.name}`
        })
      }
    })

    return {
      existingList: existingResults,
      newList: newResults
    }
  }

  return (
    <>
      <Heading level={5} weight={700} marginBottom={1}>
        Create a Check-Ins event
      </Heading>
      <Text marginBottom={1.5}>
        Get the full Check-Ins experience by connecting your Signup with a Check-Ins event. This
        event will allow you to utilize features such as attendance, labels, check-outs, trusted
        people, and pre-check in the Church Center app. After your event, all reporting will be
        available in your Check-Ins event; attendance records will not be shown in Registrations.{" "}
      </Text>
      <CheckInsList existingList={existingLegacyEvents} newList={newEvents} />
    </>
  )
}

LegacySessions.propTypes = {
  apiBaseUrl: string,
  checkInsBaseUrl: string,
  event: shape({
    id: number,
    name: string
  }),
  filterOptions: shape({
    attendanceReport: array,
    assignment: array
  }),
  checkInsPermissions: string
}

export default LegacySessions
